import { useField } from "formik";
import Select from "../select";

import { IProps } from "./select-field.types";

/** Props for {@link SelectFieldCore}: the same field props, but with `onValueChange` required rather than optional
 * (the caller has already decided this is the controlled branch) and an `isInvalid` flag for the hidden-input
 * `data-error` marker that Formik normally derives from field meta. */
type ICoreProps<IItem, IValue> = Omit<IProps<IItem, IValue>, "fieldProps" | "onValueChange"> & {
    readonly onValueChange: NonNullable<IProps<IItem, IValue>["onValueChange"]>;
    readonly isInvalid?: boolean;
};

/**
 * The select field without any form library: the value comes in through props and changes go out through
 * `onValueChange`. Hidden inputs are also added so it behaves like a proper form control. This technique was
 * inspired by React Select.
 * @param props.value         The selected value(s).
 * @param props.onValueChange Called with the new value(s) and the selected items.
 * @param props.name          Field name, used as the input ID and for the hidden inputs.
 * @param selectProps         Other props to pass down to the select component.
 * @returns                   The select, plus a hidden input per selected value.
 */
const SelectFieldCore = <IItem extends {}, IValue extends {} = string>({
    value,
    onValueChange,
    name,
    onSelect,
    isMultipleSelectCheckbox,
    enableAutoFocus = true,
    labelledBy,
    isInvalid = false,
    ...selectProps
}: ICoreProps<IItem, IValue>) => {
    const values = Array.isArray(value) ? value : [value];

    return (
        <>
            <Select<IItem, IValue>
                labelledBy={labelledBy}
                inputId={name}
                onSelect={(newValue: IValue | IValue[] | null, items: IItem | IItem[] | null) => {
                    // Single select hands over one item (or null when cleared); a controlled consumer always gets
                    // an array, so `null`/`undefined` becomes an empty array rather than `[null]`.
                    onValueChange(newValue, Array.isArray(items) ? items : items == null ? [] : [items]);
                    if (typeof onSelect === "function") {
                        // `onSelect` (the field's own prop) always expects an array; only `Select`'s callback above
                        // hands over a single item for a single select, hence the cast at this call boundary.
                        onSelect(items as IItem[]);
                    }
                }}
                selectedValues={value as IValue | IValue[] | null}
                {...(selectProps as any)}
                inputProps={{
                    ...selectProps.inputProps,
                    ...(isInvalid ? { "data-error": true } : {}),
                }}
                isMultipleSelectCheckbox={isMultipleSelectCheckbox}
                enableAutoFocus={enableAutoFocus}
            />

            {values.map((value) => (
                <input
                    key={String(value)}
                    name={name}
                    aria-hidden="true"
                    type="hidden"
                    value={typeof value === "undefined" || value === null ? "" : String(value)}
                    aria-required={selectProps.required}
                />
            ))}
        </>
    );
};

/**
 * The select field bound to a Formik field by `name`.
 * @param props.name       Formik field name.
 * @param props.fieldProps Extra `useField` configuration.
 * @param props            Other props to pass down to the select field core.
 * @returns                The select field core, bound to the Formik field's value.
 */
const SelectFieldFormik = <IItem extends {}, IValue extends {} = string>({
    fieldProps,
    name,
    onClose,
    ...props
}: IProps<IItem, IValue>) => {
    const [{ value }, meta, { setValue, setTouched }] = useField<IValue | IValue[]>({ ...(fieldProps as any), name });

    return (
        <SelectFieldCore<IItem, IValue>
            {...props}
            name={name}
            value={value}
            onValueChange={(newValue) => setValue(newValue as IValue | IValue[])}
            onClose={() => {
                onClose?.();
                setTouched(true);
            }}
            isInvalid={meta.touched && !!meta.error}
        />
    );
};

/**
 * Select field. Controlled when `onValueChange` is passed, otherwise bound to the Formik field named by `name`. A
 * plain `onChange` (e.g. from a Formik `{...field}` spread, whose `onChange` is `handleChange`) is never treated
 * specially and is left to pass through like any other prop, so it can never switch a selector into controlled mode.
 * @param props Select field props.
 * @returns     The controlled select field core, or the Formik-bound select field.
 */
const SelectField = <IItem extends {}, IValue extends {} = string>(props: IProps<IItem, IValue>) => {
    const { onValueChange, fieldProps, ...otherProps } = props;

    if (typeof onValueChange === "function") {
        return <SelectFieldCore<IItem, IValue> {...otherProps} onValueChange={onValueChange} />;
    }

    return <SelectFieldFormik<IItem, IValue> {...props} />;
};

export default SelectField;
